/*
 * ******************************************************
 * Copyright (C) 1994-2021 Babiel GmbH <info@babiel.com>
 *
 * This file is part of a Babiel development
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted without the express
 * permission of Babiel GmbH
 *
 * Any modification is not permitted without the express
 * permission of Babiel GmbH
 *
 * The file may contain confidential and or privileged materials.
 * Any review, retransmission, dissemination or other use of
 * or taking of any action in reliance upon, this file
 * by persons or entities other than the intended is prohibited.
 * ******************************************************
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class RevealHelper {
  private static readonly revealSelector: string = '[data-js-reveal]';
  private static readonly revealClass: string = 'a-reveal';
  private static readonly revealedClass: string = '-revealed';
  private static readonly delayBy: number = 200; // .2s
  private static delay: number = 0;

  private static isElementBelowViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();

    return rect.top >= (window.innerHeight || document.documentElement.clientHeight);
  }

  private static onTransitionEnd(revealElement: HTMLElement) {
    const { revealClass, revealedClass, delayBy } = RevealHelper;

    revealElement.classList.remove(revealClass, revealedClass);
    RevealHelper.delay = Math.max(RevealHelper.delay - delayBy, 0);
  }

  private static onScrollTriggerEnter(scrollTriggerInstance: gsap.plugins.ScrollTriggerInstance): void {
    const { revealedClass, onTransitionEnd, delayBy } = RevealHelper;
    const revealElement = scrollTriggerInstance.trigger as HTMLElement;

    window.setTimeout(() => {
      revealElement.classList.add(revealedClass);
      revealElement.ontransitionend = transitionEnd;
      revealElement.ontransitioncancel = transitionEnd;
    }, RevealHelper.delay);

    RevealHelper.delay += delayBy;

    function transitionEnd(): void {
      onTransitionEnd(revealElement);
      revealElement.removeEventListener('transitionEnd', this);
      revealElement.removeEventListener('onanimationcancel', this);
    }
  }

  private static initReveal(): void {
    const { revealSelector, revealClass } = RevealHelper;

    gsap.utils.toArray(revealSelector).forEach((revealElement: HTMLElement) => {
      if (!RevealHelper.isElementBelowViewport(revealElement)) {
        return;
      }

      revealElement.classList.add(revealClass);

      ScrollTrigger.create({
        trigger: revealElement,
        start: 'top 100%',
        end: '+=1px',
        once: true,
        scrub: true,
        onEnter: RevealHelper.onScrollTriggerEnter,
      });
    });
  }

  public static init(): void {
    RevealHelper.initReveal();
  }
}
